import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';
import { RiLoader2Line, RiLockLine, RiInformationLine, RiCloseLine } from '@remixicon/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { api } from '@/lib/api';

const databaseSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['postgres', 'mysql', 'sqlite']),
  host: z.string().optional(),
  port: z.coerce.number().optional(),
  database: z.string().min(1, 'Database name is required'),
  username: z.string().optional(),
  password: z.string().optional(),
  ssl: z.boolean().default(false),
  filePath: z.string().optional(),
});

type DatabaseFormData = z.infer<typeof databaseSchema>;

interface DatabaseConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  userId: string;
}

const quickTemplates = [
  {
    name: 'Local PostgreSQL',
    type: 'postgres' as const,
    host: 'localhost',
    port: 5432,
    username: 'postgres',
    ssl: false,
  },
  {
    name: 'Local MySQL',
    type: 'mysql' as const,
    host: 'localhost',
    port: 3306,
    username: 'root',
    ssl: false,
  },
  {
    name: 'Local SQLite',
    type: 'sqlite' as const,
    filePath: './database.db',
  },
];

export function DatabaseConnectionDialog({
  open,
  onOpenChange,
  onSuccess,
  userId,
}: DatabaseConnectionDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionString, setConnectionString] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [autoParsed, setAutoParsed] = useState(false);

  const form = useForm<DatabaseFormData>({
    resolver: zodResolver(databaseSchema),
    defaultValues: {
      name: '',
      type: 'postgres',
      host: 'localhost',
      port: 5432,
      database: '',
      username: '',
      password: '',
      ssl: false,
      filePath: '',
    },
  });

  const databaseType = form.watch('type');

  const applyTemplate = (template: typeof quickTemplates[0]) => {
    form.setValue('type', template.type);
    if (template.host) form.setValue('host', template.host);
    if (template.port) form.setValue('port', template.port);
    if (template.username) form.setValue('username', template.username);
    if (template.filePath) form.setValue('filePath', template.filePath);
    form.setValue('ssl', template.ssl || false);
    form.setValue('name', template.name);
    setTestStatus('idle');
    toast.success(`Applied ${template.name} template`);
  };

  const handleTestConnection = async () => {
    const values = form.getValues();

    // Validate required fields
    if (!values.database) {
      toast.error('Database name is required');
      return;
    }

    if (values.type !== 'sqlite' && !values.host) {
      toast.error('Host is required for PostgreSQL and MySQL');
      return;
    }

    if (values.type === 'sqlite' && !values.filePath) {
      toast.error('File path is required for SQLite');
      return;
    }

    setIsTesting(true);
    setTestStatus('idle');
    try {
      const result = await api.testConnection({
        type: values.type,
        host: values.host,
        port: values.port,
        database: values.database,
        username: values.username,
        password: values.password,
        ssl: values.ssl,
        filePath: values.filePath,
      });

      if (result.success) {
        setTestStatus('success');
        setTestMessage(result.details?.version ? `Connected • ${result.details.version}` : 'Connection successful');
        toast.success(result.message, {
          description: result.details?.version
            ? `Version: ${result.details.version}`
            : undefined,
        });
      } else {
        setTestStatus('error');
        setTestMessage(result.message);
        toast.error(result.message, {
          description: result.details?.code || result.details?.sqlState
            ? `Error: ${result.details.code || result.details.sqlState}`
            : undefined,
        });
      }
    } catch (error: any) {
      setTestStatus('error');
      setTestMessage(error.message || 'Connection failed');
      toast.error('Failed to test connection', {
        description: error.message,
      });
    } finally {
      setIsTesting(false);
    }
  };

  const parseConnectionString = (connStr: string, showToast = true) => {
    try {
      const url = new URL(connStr);

      let protocol = url.protocol.replace(':', '');
      let type: 'postgres' | 'mysql' | 'sqlite';

      if (protocol === 'postgresql' || protocol === 'postgres') {
        type = 'postgres';
      } else if (protocol === 'mysql') {
        type = 'mysql';
      } else if (protocol === 'sqlite') {
        type = 'sqlite';
      } else {
        if (showToast) {
          toast.error('Unsupported database type. Use postgresql, mysql, or sqlite.');
        }
        return false;
      }

      const host = url.hostname;
      const port = url.port ? parseInt(url.port) : (type === 'postgres' ? 5432 : 3306);
      const username = url.username;
      const password = url.password;
      const database = url.pathname.slice(1);
      const ssl = url.searchParams.get('ssl') === 'true' || url.searchParams.get('sslmode') === 'require';

      // Set database type FIRST so the form updates properly
      form.setValue('type', type);

      // Then set other fields
      form.setValue('host', host);
      form.setValue('port', port);
      form.setValue('username', username);
      form.setValue('password', password);
      form.setValue('database', database);
      form.setValue('ssl', ssl);

      if (!form.getValues('name')) {
        form.setValue('name', `${host}/${database}`);
      }

      if (showToast) {
        toast.success(`Parsed ${type.toUpperCase()} connection string`);
      }
      setConnectionString('');
      setTestStatus('idle');
      setAutoParsed(true);
      return true;
    } catch (error) {
      if (showToast) {
        toast.error('Invalid connection string format');
      }
      return false;
    }
  };

  // Auto-parse connection string when it looks complete
  const handleConnectionStringChange = (value: string) => {
    setConnectionString(value);
    setAutoParsed(false);

    // Auto-parse if it looks like a complete connection string
    // Check for protocol:// pattern
    if (value.includes('://') && value.length > 15) {
      const parsed = parseConnectionString(value, false);
      if (parsed) {
        toast.success('Auto-parsed connection string!', {
          description: 'Fields have been auto-filled below',
        });
      }
    }
  };

  const onSubmit = async (data: DatabaseFormData) => {
    setIsLoading(true);
    try {
      await api.connectDatabase({
        ...data,
        userId,
      });

      toast.success('Database connected successfully!');
      form.reset();
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Failed to connect to database');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect Database</DialogTitle>
          <DialogDescription>
            Configure your database connection
          </DialogDescription>
        </DialogHeader>

        <TooltipProvider>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* Connection String Parser */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FormLabel>Connection String (Optional)</FormLabel>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <RiInformationLine className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Paste your database URL to auto-fill the form fields below</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="postgresql://user:password@host:5432/database"
                    value={connectionString}
                    onChange={(e) => handleConnectionStringChange(e.target.value)}
                    className="pr-8 font-mono text-xs"
                    disabled={isLoading}
                  />
                  {connectionString && (
                    <button
                      type="button"
                      onClick={() => {
                        setConnectionString('');
                        setAutoParsed(false);
                      }}
                      className="absolute transition-colors -translate-y-1/2 right-2 top-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <RiCloseLine className="w-4 h-4" />
                    </button>
                  )}
                  {autoParsed && !connectionString && (
                    <div className="absolute -translate-y-1/2 right-2 top-1/2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => parseConnectionString(connectionString, true)}
                  disabled={!connectionString || isLoading}
                  size="sm"
                >
                  Parse
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-background text-muted-foreground">
                  or fill manually
                </span>
              </div>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Connection Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="My Production Database"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Database Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isLoading}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select database type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="postgres">PostgreSQL</SelectItem>
                      <SelectItem value="mysql">MySQL</SelectItem>
                      <SelectItem value="sqlite">SQLite</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {databaseType !== 'sqlite' ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="host"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Host</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="localhost"
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="port"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Port</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder={databaseType === 'postgres' ? '5432' : '3306'}
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="database"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Database Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="myapp_production"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Username</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={databaseType === 'postgres' ? 'postgres' : 'root'}
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={databaseType === 'postgres' ? 'postgres' : 'root'}
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  /> */}

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        {/* <div className="flex items-center gap-2"> */}
                          <FormLabel>Password</FormLabel>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {/* <RiLockLine className="h-3.5 w-3.5 text-muted-foreground cursor-help" /> */}
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">Your password is encrypted and stored securely. No one can view your credentials.</p>
                            </TooltipContent>
                          </Tooltip>
                        {/* </div> */}
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="••••••••"
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="ssl"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center p-4 space-x-3 space-y-0 border rounded-lg border-border/60 bg-card/50">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <div className="space-y-0.5 leading-none">
                        <FormLabel className="text-sm font-medium">Enable SSL Connection</FormLabel>
                        <FormDescription className="text-xs text-muted-foreground">
                          Use encrypted connection for secure data transfer
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </>
            ) : (
              <FormField
                control={form.control}
                name="filePath"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File Path</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="./database.db"
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Path to your SQLite database file
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Test Connection Status */}
            {testStatus !== 'idle' && (
              <div className={`flex items-center gap-2 p-3 rounded-lg border ${testStatus === 'success' ? 'border-green-500/50 bg-green-50 dark:bg-green-950/20' : 'border-red-500/50 bg-red-50 dark:bg-red-950/20'}`}>
                {testStatus === 'success' ? (
                  <CheckCircle2 className="flex-shrink-0 text-green-600 size-4 dark:text-green-400" />
                ) : (
                  <XCircle className="flex-shrink-0 text-red-600 size-4 dark:text-red-400" />
                )}
                <span className={`text-sm ${testStatus === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {testMessage}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={isLoading || isTesting}
                className="flex-1"
              >
                {isTesting ? (
                  <>
                    <RiLoader2Line className="mr-2 size-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>
              <Button
                type="submit"
                disabled={isLoading || isTesting || testStatus === 'error'}
                className="flex-1"
              >
                {isLoading ? (
                  <>
                    <RiLoader2Line className="mr-2 size-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect'
                )}
              </Button>
            </div>
          </form>
        </Form>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
